/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * One recipe: scale it, check it for conflicts, cook it.
 *
 * Scaling here calls the SERVER (`/scaled`) rather than duplicating the maths
 * in the browser. The iOS app ports scaling.ts locally because a stepper drag
 * needs to feel instant on a phone; on the web a fetch per step is fine and
 * one implementation is one fewer thing to drift.
 */

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CookApi, RecipesApi, SettingsApi, ThreadsApi } from "../../api/endpoints.ts";
import type { AllergenFix } from "../../api/types.ts";
import { usePrefs } from "../../i18n/index.tsx";
import { AllergenBadge, Banner, ErrorBox, Loading, Modal } from "../../components/ui.tsx";
import { Icon } from "../../components/Icon.tsx";
import { ActionMenu } from "../../components/ActionMenu.tsx";
import { useToast } from "../../components/Toast.tsx";
import { AddToPlanModal } from "./AddToPlanModal.tsx";
import { NutritionPanel } from "./NutritionPanel.tsx";
import { RecipeImage } from "./RecipeImage.tsx";
import { RecipeNotesPanel } from "./RecipeNotesPanel.tsx";
import { RecipeThreadPanel } from "./RecipeThreadPanel.tsx";

export function RecipeDetailView() {
  const { id = "" } = useParams();
  const { t } = usePrefs();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [servings, setServings] = useState<number | null>(null);
  const [cooking, setCooking] = useState<string | null>(null);
  const [planning, setPlanning] = useState(false);

  const refreshRecipe = () => {
    void qc.invalidateQueries({ queryKey: ["recipes", id] });
    void qc.invalidateQueries({ queryKey: ["recipes"] });
  };

  const favourite = useMutation({
    mutationFn: (next: boolean) => RecipesApi.patch(id, { isFavorite: next }),
    onSuccess: refreshRecipe,
    onError: (e: Error) => toast(e.message, "danger"),
  });
  const duplicate = useMutation({
    mutationFn: () => RecipesApi.duplicate(id),
    onSuccess: (r) => { toast("Duplicated", "ok"); navigate(`/recipes/${r.id}`); },
    onError: (e: Error) => toast(e.message, "danger"),
  });
  // Archive, not hard-delete: a recipe on a past meal plan is part of the
  // household's cook history, and removing it would rewrite that.
  const archive = useMutation({
    mutationFn: () => RecipesApi.remove(id),
    onSuccess: () => { toast("Archived", "ok"); navigate("/recipes"); },
    onError: (e: Error) => toast(e.message, "danger"),
  });

  const recipe = useQuery({ queryKey: ["recipes", id], queryFn: () => RecipesApi.get(id) });
  const target = servings ?? recipe.data?.servings_base ?? 4;

  const scaled = useQuery({
    queryKey: ["recipes", id, "scaled", target],
    queryFn: () => RecipesApi.scaled(id, target),
    enabled: !!recipe.data,
  });

  const settings = useQuery({ queryKey: ["settings"], queryFn: () => SettingsApi.get() });

  const conflicts = useQuery({
    queryKey: ["recipes", id, "conflicts"],
    queryFn: () => RecipesApi.conflicts(id),
    enabled: !!recipe.data,
  });

  // Cheap, and answers four states at once (active / invited / left / never
  // offered). The panel decides whether to render anything.
  const thread = useQuery({
    queryKey: ["recipes", id, "thread"],
    queryFn: () => ThreadsApi.state(id),
    enabled: !!recipe.data,
  });

  const start = useMutation({
    mutationFn: () => CookApi.start(id, { servings: target }),
    onSuccess: (s) => setCooking(s.session.id),
  });

  const logCooked = useMutation({
    // `target` is the scaled serving count on screen, so the cook log records
    // what was actually made rather than the recipe's base.
    mutationFn: () => CookApi.logCooked(id, { servings: target }),
    onSuccess: () => { toast("Logged", "ok"); refreshRecipe(); },
    onError: (e: Error) => toast(e.message, "danger"),
  });

  if (recipe.isLoading) return <Loading />;
  if (recipe.error) return <ErrorBox message={(recipe.error as Error).message} />;
  if (!recipe.data) return null;
  const r = recipe.data;

  return (
    <div className="max-w-3xl space-y-5">
      <Link to="/recipes" className="inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink">
        <Icon name="chevronLeft" className="h-4 w-4" /> {t("nav.recipes")}
      </Link>

      {/*
        ── THE ACTION ROW MOVED BELOW THE TITLE ──────────────────────────────
        Six buttons used to sit ABOVE the recipe's own name — so the screen
        offered you six verbs before it told you what they applied to. All six
        were `btn-ghost text-xs`, which meant "Add to plan" (the entire point of
        a meal planner) had precisely the same visual weight as "Duplicate",
        and the destructive "Archive" sat in the same row at the same weight as
        both.

        Now: the recipe identifies itself first, then one primary action, a
        favourite toggle, and everything else behind ⋯ with Archive separated
        below a rule.
      */}
      {/* The hero, when there is one. Above the title rather than beside it:
          a recipe you're about to cook is a thing you look at first and read
          second, and a 3:2 photo at full column width is the closest this app
          gets to a cookbook spread. Absent, the title simply leads — no
          placeholder, no reserved space. */}
      {r.has_image && (
        <RecipeImage recipeId={r.id} alt={r.title} aspect="hero" />
      )}

      <header className="space-y-2">
        <h1 className="type-display">{r.title}</h1>
        {r.description && <p className="text-ink-2">{r.description}</p>}
        <div className="flex flex-wrap items-center gap-2 text-sm text-ink-3">
          {r.total_minutes ? <span>{r.total_minutes} {t("common.minutes")}</span> : null}
          {/* Computed server-side in the household timezone. */}
          <span>· {r.last_cooked_label}</span>
          <span>· made {r.times_cooked}×</span>
        </div>
        <AllergenBadge tags={r.allergen_tags} partial={r.allergens_partial} />
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-primary" onClick={() => setPlanning(true)}>
          <Icon name="calendarPlus" className="h-4 w-4" />
          Add to plan
        </button>
        <button
          className="btn-ghost"
          onClick={() => favourite.mutate(!r.is_favorite)}
          aria-pressed={r.is_favorite}
        >
          <Icon name={r.is_favorite ? "starFilled" : "star"}
                className={`h-4 w-4 ${r.is_favorite ? "text-accent" : ""}`} />
          {r.is_favorite ? "Favourite" : "Favourite"}
        </button>
        <ActionMenu
          items={[
            {
              label: "I cooked this",
              icon: "check",
              hint: `Logs it for ${target} servings`,
              onSelect: () => logCooked.mutate(),
            },
            { label: "Edit", icon: "pencil", onSelect: () => navigate(`/recipes/${id}/edit`) },
            {
              label: "Duplicate",
              icon: "copy",
              hint: "A separate copy you can change freely",
              onSelect: () => duplicate.mutate(),
            },
            {
              label: "Archive",
              icon: "archive",
              destructive: true,
              // Not a delete, and the menu says so. A recipe on a past meal
              // plan is part of the household's cook history.
              hint: "Hidden from the library, kept in your cook history",
              onSelect: () => {
                if (confirm(`Archive "${r.title}"?`)) archive.mutate();
              },
            },
          ]}
        />
      </div>

      {conflicts.data && conflicts.data.conflicts.length > 0 && (
        <Banner tone="danger">
          <div>{conflicts.data.summary}</div>
          {/* The swap, spelled out under the warning rather than replacing it.
              "Contains gluten" stays true of standard soy sauce; this is the
              line that tells you what to buy instead. */}
          {(() => {
            const fixes: AllergenFix[] =
              conflicts.data.conflicts.flatMap((c) => c.fixes ?? []);
            const seen = new Set<string>();
            const unique = fixes.filter((f: AllergenFix) => {
              const k = `${f.from}→${f.to}`;
              if (seen.has(k)) return false;
              seen.add(k);
              return true;
            });
            if (unique.length === 0) return null;
            return (
              <ul className="mt-2 space-y-1 text-sm">
                {unique.map((f) => (
                  <li key={`${f.from}-${f.to}`}>
                    <span className="font-medium">{f.from} → {f.to}</span>
                    <span className="text-ink-2"> · {f.note}</span>
                  </li>
                ))}
              </ul>
            );
          })()}
        </Banner>
      )}
      {conflicts.data?.partial && !conflicts.data.conflicts.length && (
        <Banner tone="warn">{conflicts.data.summary}</Banner>
      )}

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <span className="text-sm">{t("common.servings")}</span>
        <button className="btn-ghost" onClick={() => setServings(Math.max(1, target - 1))}>−</button>
        <span className="min-w-8 text-center text-lg font-medium">{target}</span>
        <button className="btn-ghost" onClick={() => setServings(target + 1)}>+</button>
        {target !== r.servings_base && (
          <span className="text-xs text-ink-3">base {r.servings_base}</span>
        )}
        <button className="btn-primary ml-auto" onClick={() => start.mutate()}
          disabled={start.isPending}>
          {t("recipes.cook")}
        </button>
        <button className="btn-ghost" onClick={() => logCooked.mutate()}
          disabled={logCooked.isPending}>
          Mark cooked
        </button>
      </div>

      {/* Advisory only, and deliberately free of a stated duration — see
          scaling.ts. */}
      {scaled.data?.timeHint && <Banner tone="warn">{scaled.data.timeHint}</Banner>}

      <section>
        <h2 className="type-section mb-3">Ingredients</h2>
        {/* Rules, not a bordered card. A card says "this is a widget"; an
            ingredient list is prose you read down, and hairlines carry the
            separation without the chrome. */}
        <ul className="divide-y divide-rule-soft border-y border-rule-soft">
          {(scaled.data?.ingredients ?? []).map((i, idx) => (
            <li key={idx} className="type-read py-2.5">
              {i.displayText}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="type-section mb-3">Method</h2>
        {/* 17px at 1.75, up from 14px at Tailwind's default 1.43.
            You read these standing up, hands occupied, phone propped against
            something on the counter. It is the single highest-value
            typographic change in the app. */}
        <ol className="space-y-5">
          {r.steps.map((s) => (
            <li key={s.id} className="flex gap-4">
              <span className="label-mono shrink-0 pt-1.5">{s.step_no}</span>
              <div className="min-w-0">
                <p className="type-read">{s.text}</p>
                {s.duration_minutes && (
                  <p className="type-meta mt-1">{s.duration_minutes} min</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Below the method, not beside the servings stepper. Nutrition is
          reference material you look up after deciding to cook something, not
          a number you weigh the dish against before reading it — putting it up
          top would turn a recipe card into a food label. It renders nothing at
          all for a household that has asked not to see figures. */}
      <NutritionPanel
        recipeId={id}
        panel={r.nutrition}
        status={r.nutrition_status}
        silent={r.nutrition_silent}
        onRefresh={() => void recipe.refetch()}
      />

      {/* Household-private notes, immediately above the cross-household
          discussion. The adjacency is the point: both labels are visible at
          once, so "which box is which" is answered by looking rather than by
          remembering. */}
      <RecipeNotesPanel recipeId={id} />

      {/* Cross-household discussion. Renders nothing at all unless a
          discussion-enabled share touched this recipe, so an ordinary private
          recipe's page is unchanged. */}
      {thread.data && (
        <RecipeThreadPanel
          recipeId={id}
          state={thread.data}
          onStateChange={() => void thread.refetch()}
        />
      )}

      {cooking && (
        <CookModal sessionId={cooking} onClose={() => {
          setCooking(null);
          void qc.invalidateQueries({ queryKey: ["recipes"] });
        }} />
      )}

      {planning && (
        <AddToPlanModal
          recipe={r}
          slots={(settings.data?.slots ?? []).map((s) => ({ key: s.key, label: s.label }))}
          onClose={() => setPlanning(false)}
          onAdded={() => void qc.invalidateQueries({ queryKey: ["plan"] })}
        />
      )}
    </div>
  );
}

/**
 * Cook Mode.
 *
 * Progress is saved SERVER-side on every step, so closing the tab mid-recipe
 * doesn't lose your place. Step timers come from the recipe and are NOT scaled.
 */
function CookModal({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const [rating, setRating] = useState<number | null>(null);

  const session = useQuery({
    queryKey: ["cook", sessionId],
    queryFn: () => CookApi.get(sessionId),
    refetchOnWindowFocus: false,
  });

  const progress = useMutation({
    mutationFn: (body: Record<string, unknown>) => CookApi.progress(sessionId, body),
    onSuccess: () => void session.refetch(),
  });

  const finish = useMutation({
    mutationFn: () => CookApi.finish(sessionId, rating ? { rating } : {}),
    onSuccess: onClose,
  });

  if (session.isLoading) return <Modal title="Cook mode" onClose={onClose}><Loading /></Modal>;
  if (!session.data) return null;
  const s = session.data;
  const step = s.steps.find((x) => x.stepNo === s.session.current_step) ?? s.steps[0];
  const atEnd = s.session.current_step >= s.steps.length;

  return (
    <Modal title={s.recipe.title} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="flex justify-between text-sm text-ink-3">
          <span>{s.servings} servings</span>
          <span>{s.session.elapsedMinutes} min elapsed</span>
        </div>

        <details className="card p-3" open>
          <summary className="cursor-pointer text-sm font-medium">Ingredients</summary>
          <ul className="mt-2 space-y-1 text-sm">
            {s.ingredients.map((i, idx) => <li key={idx}>• {i.displayText}</li>)}
          </ul>
        </details>

        {step && (
          <div className="space-y-2">
            <div className="label-mono">Step {step.stepNo} of {s.steps.length}</div>
            <p className="type-title">{step.text}</p>
            {step.durationMinutes && (
              <p className="text-sm text-accent">⏱ {step.durationMinutes} min</p>
            )}
          </div>
        )}

        {atEnd ? (
          <div className="space-y-3">
            <p className="text-sm">How was it?</p>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} className="text-2xl" onClick={() => setRating(n)}
                  aria-label={`${n} stars`}>
                  {(rating ?? 0) >= n ? "★" : "☆"}
                </button>
              ))}
            </div>
            <button className="btn-primary w-full" onClick={() => finish.mutate()}
              disabled={finish.isPending}>
              Finish
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button className="btn-ghost"
              disabled={s.session.current_step <= 1}
              onClick={() => progress.mutate({
                currentStep: s.session.current_step - 1,
                completedSteps: s.session.completed_steps,
              })}>
              Back
            </button>
            <button className="btn-primary flex-1"
              onClick={() => progress.mutate({
                currentStep: s.session.current_step + 1,
                completedSteps: [...new Set([...s.session.completed_steps, s.session.current_step])],
              })}>
              Next step
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
